import React, { useState, useEffect } from "react";
import { useFormOrchestrator } from "../../FormContext/useFormContext";
import CustomCardList from "@components/PatientForms/common/CustomCardList";
import { FormField } from "@components/FormEngine/types";
import { CategoryType } from "../../../../types/PatientFormTypes";
import MedicationsAutoComplete from "./MedicationsAutoComplete";
import { z } from "zod";
import medicationService from "src/services/medicationService";
import { getRecordId } from "@utils/helpers";
import _ from "lodash";

type CardField = Exclude<FormField, { type: "group-input" }>;

const medicationsFields: CardField[] = [
	{
		type: "custom",
		component: MedicationsAutoComplete,
		name: "name",
		label: "Medication Name",
		fieldProps: {
			prefix: "activeMedications",
		},
		rule: z.string().min(1, "Medication name is required"),
	},
  {
    type: "input",
    name: "strength",
    label: "Dosage",
		fieldProps: {
			InputLabelProps: {
				shrink: true,
			},
		},
		rule: z.string().min(1, "Dosage is required"),
	},
  {
    type: "input",
    name: "frequency",
    label: "How often do you take it?",
		fieldProps: {
			InputLabelProps: {
				shrink: true,
			},
		},
		rule: z.string().min(1, "Frequency is required"),
  },
  {
    type: "date",
    name: "startDate",
    label: "Start Date",
    fieldProps: {
      formatValue: "MM/DD/YYYY",
			disableFuture: true,
    },
		rule: z.coerce.date().max(new Date(), "Start date must be in the past"),
  },
];

const newMedication = {
  name: "",
  strength: "",
  frequency: "",
  startDate: null,
};

export const MedicationsList: React.FC = () => {
  const { formState, saveCategoryData, loadStepData, setStepData } = useFormOrchestrator();
  const patientId = formState.patientId;
  const category: CategoryType = "medications";

  const onSaveItem = async (data: any, type: 'add' | 'edit' | 'delete') => {
    try {
      switch (type) {
        case 'add':
          await medicationService.addMedication(patientId, data);
          break;
        case 'edit':
          await medicationService.updateMedication(patientId, data);
          break;
        case 'delete':
          const id = getRecordId(data);
          if (!id) throw new Error("Could not find ID for medication to delete.");
          await medicationService.deleteMedication(patientId, id);
          break;
      }

      const newData = await loadStepData(formState.currentStepIndex);
      setStepData(newData);

    } catch (error) {
      console.error(`Error during medication ${type} operation:`, error);
    }
  }

  return (
    <CustomCardList
      name="activeMedications"
      label="Active Medications"
      addButtonText="Medication"
      cardFields={medicationsFields}
      category={category}
      onSaveCategoryData={saveCategoryData}
      onSaveItem={onSaveItem}
      newItemTemplate={newMedication}
    />
  );
};

export const MedicationAllergiesList: React.FC = () => {
  const { saveCategoryData } = useFormOrchestrator();
  const category: CategoryType = "medications";

  return (
    <CustomCardList
      name="medicationAllergies"
      label="Medication Allergies"
      addButtonText="Medication Allergy"
      cardFields={[
				{
					type: "custom",
					component: MedicationsAutoComplete,
					name: "name",
					label: "Medication Allergy Name",
					fieldProps: {
						prefix: "medicationAllergies",
						updateOtherFields: false,
					},
				}
			]}
      category={category}
      onSaveCategoryData={saveCategoryData}
      newItemTemplate={{ name: "" }}
			hideFieldData
    />
  );
};

export default MedicationsList;
